import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2 } from 'lucide-react';

// UI Components
import { Form } from '@/components/ui/form';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import BusinessInfoFields from '@/components/settings/BusinessInfoFields';
import DangerZone from '@/components/settings/DangerZone';
import AnchorBillingSettings from '@/components/SettingsTabs/AnchorBillingSettings';

// Hooks & Services
import { useToast } from '@/hooks/use-toast';
import { useStripeStatus } from '@/hooks/useStripeStatus';
import { useStripeBusinessSync } from '@/hooks/useStripeBusinessSync';
import { supabase } from '@/integrations/supabase/client';
import { ONBOARDING_STORAGE_KEY } from '@/hooks/onboarding/useOnboardingState';

// Types
import { BusinessProfile } from '@/types/businessProfile';

const formSchema = z.object({
  businessName: z.string().min(1, 'Business name is required'),
  streetAddress: z.string().min(1, 'Street address is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zipCode: z.string().min(1, 'ZIP code is required'),
  subdomain: z.string().optional(),
  replyEmail: z.string().email('Invalid email address').optional().or(z.literal(''))
});

type FormValues = z.infer<typeof formSchema>;

interface BusinessProfileSettingsProps {
  onFormStateChange?: (isDirty: boolean, submitHandler: () => void) => void;
}

const BusinessProfileSettings: React.FC<BusinessProfileSettingsProps> = ({ onFormStateChange }) => {
  // Hooks
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const { accountId: stripeAccountId, isConnected } = useStripeStatus();
  const { syncStripeBusinessInfo, isLoading: isSyncing } = useStripeBusinessSync();

  // Form setup
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      businessName: '',
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      subdomain: '',
      replyEmail: ''
    },
  });

  // Form submission handler
  const onSubmit = useCallback(async (values: FormValues) => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (!user) return;
      
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('ref_signup')
        .eq('id', user.id)
        .single();

      const updateData: any = {
        business_name: values.businessName,
        street_address: values.streetAddress,
        city: values.city,
        state: values.state,
        zip_code: values.zipCode, 
        subdomain: values.subdomain,
        reply_email: values.replyEmail || null,
        updated_at: new Date().toISOString()
      };

      // Preserve ref_signup if it exists
      if (currentProfile?.ref_signup) {
        updateData.ref_signup = currentProfile.ref_signup;
      }
      
      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) {
        throw error;
      }

      // Reset form to mark it as not dirty after successful save
      form.reset(values, { keepValues: true });

      toast({
        title: "Business information updated",
        description: "Your business information has been saved.",
        variant: "success",
        className: "border-none"
      });

      // Dispatch event to notify onboarding checklist of the update
      const businessProfileUpdatedEvent = new CustomEvent('business-profile-updated', {
        detail: { profileData: values }
      });
      document.dispatchEvent(businessProfileUpdatedEvent);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred.";
      console.error('Error saving business information:', errorMessage);
      toast({
        title: "Failed to update business information",
        description: errorMessage,
        variant: "destructive",
      });
    }
  }, [form, toast]);

  // Expose form state to parent
  const isDirty = form.formState.isDirty;
  
  // Memoize submit handler to avoid recreating on every render
  const handleSubmit = useCallback(() => {
    form.handleSubmit(onSubmit)();
  }, [form, onSubmit]);
  
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(isDirty, handleSubmit);
    }
  }, [isDirty, onFormStateChange, handleSubmit]);

  // Fetch business information from profiles table
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        setLoading(true);
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (!user) return;
        
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching business profile:', error);
          toast({
            title: "Failed to load business information",
            description: "Please try again later.",
            variant: "destructive",
          });
          return;
        }

        // Populate form with existing data
        if (profile) {
          // Cast the profile to our BusinessProfile type
          const businessProfile = profile as unknown as BusinessProfile;
          
          form.reset({
            businessName: businessProfile.business_name || '',
            streetAddress: businessProfile.street_address || '',
            city: businessProfile.city || '',
            state: businessProfile.state || '',
            zipCode: businessProfile.zip_code || '',
            subdomain: businessProfile.subdomain || '',
            replyEmail: businessProfile.reply_email || ''
          });
        }
      } catch (error) {
        console.error('Error in fetchBusinessInfo:', error);
        toast({
          title: "Failed to load profile data",
          description: "Please try again later.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessInfo();
  }, [form, toast]);

  // Sync business info from Stripe
  const handleSyncFromStripe = async () => {
    const businessInfo = await syncStripeBusinessInfo();
    
    if (businessInfo) {
      form.reset({
        businessName: businessInfo.name || '',
        streetAddress: businessInfo.address?.line1 || '',
        city: businessInfo.address?.city || '',
        state: businessInfo.address?.state || '',
        zipCode: businessInfo.address?.postal_code || '',
        subdomain: form.getValues('subdomain'), // Keep existing subdomain
        replyEmail: form.getValues('replyEmail')
      });
    }
  };

  // Reset user data handler
  const handleResetUserData = async () => {
    setIsResetting(true);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error("No active session");
      }

      // Call the edge function
      const { data, error } = await supabase.functions.invoke('reset-user-data', {
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message || "Failed to reset user data");
      }

      if (!data?.success) {
        throw new Error(data?.error || "Failed to reset user data");
      }

      // Clear localStorage onboarding state
      const storageKey = `${ONBOARDING_STORAGE_KEY}_${user.id}`;
      localStorage.removeItem(storageKey);

      // Reset form to empty values
      form.reset({
        businessName: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        subdomain: '',
        replyEmail: ''
      });

      toast({
        title: "Data reset successfully",
        description: "All your data has been reset. You can now start fresh with the onboarding process.",
        variant: "success",
      });

      // Dispatch event to notify onboarding checklist
      const resetEvent = new CustomEvent('user-data-reset');
      document.dispatchEvent(resetEvent);

      // Reload the page to refresh all data
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred.";
      console.error('Error resetting user data:', errorMessage);
      toast({
        title: "Failed to reset user data",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
    }
  };
  const env = import.meta.env.VITE_APP_ENV;

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2">Loading business information...</span>
      </div>
    );
  }

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 overflow-auto">
          <Card className="bg-gray-50 border-gray-200">
            <CardContent>
              <BusinessInfoFields form={form} />
            </CardContent>
          </Card>
          

          <Separator />

          <Card className="bg-gray-50 border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg">Email used to receive responses</CardTitle>
              <CardDescription>
                Emails are sent from KeepMore's domain. Replies will go to this address.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="replyEmail"
                rules={{
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Invalid email address'
                  }
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reply-To email</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" placeholder="support@yourbusiness.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {isConnected && (
            <>
              <Separator />
              <AnchorBillingSettings />
            </>
          )}
        </form>
      </Form>
      
      <Separator />

      {env !== 'production' && (
        <DangerZone isResetting={isResetting} onReset={handleResetUserData} />
      )}
    </div>
  );
};

export default BusinessProfileSettings;